import * as React from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { registerWithEmailAndPassword } from "../API/firebase";
import MySnackbar from "./snackBar";
import { PillButton } from "./ui";
import { paths } from "../routes/paths";

const MIN_PASSWORD = 8;

export default function SignUp() {
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const navigate = useNavigate();

  const errors = {
    name: name.trim().length < 2,
    company: company.trim().length === 0,
    email: !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()),
    password: password.length < MIN_PASSWORD,
    // Compares the values. This previously compared only their lengths, so two
    // different passwords of equal length were accepted as matching.
    password2: password2 !== password,
  };
  const hasErrors = Object.values(errors).some(Boolean);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitted(true);
    if (hasErrors) return;

    setBusy(true);
    try {
      await registerWithEmailAndPassword(
        name.trim(),
        company.trim(),
        email.trim(),
        password
      );
      navigate(paths.home);
    } catch (error: unknown) {
      const code = typeof error === "string" ? error : "";
      setSnackbarMessage(
        code === "auth/email-already-in-use"
          ? "That email address already has an account."
          : code === "auth/weak-password"
          ? "Choose a stronger password."
          : "Something went wrong. Please try again."
      );
      setOpenSnackbar(true);
    } finally {
      setBusy(false);
    }
  };

  const show = (field: keyof typeof errors) => submitted && errors[field];

  return (
    <>
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <TextField
          margin="normal"
          required
          fullWidth
          id="name"
          label="Your name"
          autoComplete="name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          error={show("name")}
          helperText={show("name") ? "Enter your name." : " "}
        />
        <TextField
          margin="normal"
          required
          fullWidth
          id="company"
          label="Company"
          autoComplete="organization"
          value={company}
          onChange={(event) => setCompany(event.target.value)}
          error={show("company")}
          helperText={
            show("company")
              ? "Enter your company."
              : "Offered as the default owner when you register a product."
          }
        />
        <TextField
          margin="normal"
          required
          fullWidth
          id="signup-email"
          label="Email address"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          error={show("email")}
          helperText={show("email") ? "Enter a valid email address." : " "}
        />
        <TextField
          margin="normal"
          required
          fullWidth
          id="signup-password"
          label="Password"
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          error={show("password")}
          helperText={
            show("password")
              ? `Use at least ${MIN_PASSWORD} characters.`
              : `At least ${MIN_PASSWORD} characters.`
          }
        />
        <TextField
          margin="normal"
          required
          fullWidth
          id="signup-password2"
          label="Repeat password"
          type="password"
          autoComplete="new-password"
          value={password2}
          onChange={(event) => setPassword2(event.target.value)}
          error={show("password2")}
          helperText={show("password2") ? "Passwords do not match." : " "}
        />

        <PillButton
          tone="amber"
          type="submit"
          fullWidth
          disabled={busy}
          sx={{ mt: 2 }}
        >
          {busy ? "Creating account…" : "Create account"}
        </PillButton>
      </Box>

      <MySnackbar
        message={snackbarMessage}
        onClose={() => setOpenSnackbar(false)}
        open={openSnackbar}
      />
    </>
  );
}
